import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Clock, Sparkles, Volume2, VolumeX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Time between each number reveal (in seconds)
const REVEAL_INTERVAL = 60; // 1 minute

const LiveDraw = () => {
  const navigate = useNavigate();
  const [drawState, setDrawState] = useState('waiting'); // waiting, drawing, complete
  const [winningNumbers, setWinningNumbers] = useState([]);
  const [revealedNumbers, setRevealedNumbers] = useState([]);
  const [countdown, setCountdown] = useState(REVEAL_INTERVAL);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [drawId, setDrawId] = useState(null);

  // Start a new draw
  const startDraw = async () => {
    try {
      // Trigger draw from admin endpoint (for demo - in production this would be automatic)
      const response = await axios.post(`${API}/admin/draws`, {
        admin_key: 'sollotto-admin-2024'
      });
      
      setWinningNumbers(response.data.winning_numbers);
      setDrawId(response.data.id);
      setDrawState('drawing');
      setRevealedNumbers([]);
      setCurrentIndex(0);
      setCountdown(REVEAL_INTERVAL);
    } catch (error) {
      console.error('Error starting draw:', error);
      alert('Could not start draw. Check admin key.');
    }
  };

  // Load latest draw if exists
  const loadLatestDraw = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/draws/latest`);
      if (response.data) {
        setWinningNumbers(response.data.winning_numbers);
        setDrawId(response.data.id);
        setRevealedNumbers(response.data.winning_numbers);
        setDrawState('complete');
      }
    } catch (error) {
      console.log('No draws yet');
    }
  }, []);

  useEffect(() => {
    loadLatestDraw();
  }, [loadLatestDraw]);

  // Countdown and reveal logic
  useEffect(() => {
    if (drawState !== 'drawing') return;
    if (currentIndex >= 6) {
      setDrawState('complete');
      return;
    }

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          // Reveal next number
          setRevealedNumbers(nums => [...nums, winningNumbers[currentIndex]]);
          setCurrentIndex(idx => idx + 1);
          
          // Play sound effect
          if (soundEnabled) {
            const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleR8NP5TT1qFzJhI/j9DRk2klFj2Lzc6IYCQYPYnLyH1ZIho8h8nDclMgHTuFx7xmTB4fOoTFtVtFHCE5gsO0UEAaIziAwbBIPRglN3++rD83FSc2fbyqNjMUKDV8u6gtMBMpNHu6pSYuEio0erqiIiwRKzN5uZ8fKhArM3m5nBwoECwzebmZGicQLDN5uZcYJhAsMni5lRYlECsyeLmTFCQQKzJ4uZETJBArMni5jxIkDysyeLmPESQPKzJ4uY8QIw8rMni5jg8jDyoyeLmODiMPKjJ4uY4OIw8qMni5jg4jDyoyeLmODiMPKjJ4uY4OIw8qMg==');
            audio.volume = 0.3;
            audio.play().catch(() => {});
          }
          
          return REVEAL_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [drawState, currentIndex, winningNumbers, soundEnabled]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="live-draw-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="w-8 h-8 text-sol-green" />
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">
              Live Draw
            </h1>
            <Sparkles className="w-8 h-8 text-sol-green" />
          </div>
          <p className="text-white/60">
            Watch the winning numbers reveal one by one
          </p>
        </motion.div>

        {/* Sound Toggle */}
        <div className="flex justify-center mb-8">
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
          >
            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            <span className="text-sm">{soundEnabled ? 'Sound On' : 'Sound Off'}</span>
          </button>
        </div>

        {/* Draw Area */}
        <motion.div
          className="card-glass rounded-2xl p-8 mb-8"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          {/* Waiting State */}
          {drawState === 'waiting' && (
            <div className="text-center py-12">
              <div className="w-24 h-24 rounded-full bg-sol-purple/20 flex items-center justify-center mx-auto mb-6">
                <Trophy className="w-12 h-12 text-sol-purple" />
              </div>
              <h2 className="font-heading text-2xl font-bold mb-4">Ready for Draw</h2>
              <p className="text-white/60 mb-8">
                Start the draw to reveal 6 winning numbers
              </p>
              <Button
                onClick={startDraw}
                className="bg-sol-green text-black hover:bg-sol-green/80 px-8 py-3 text-lg"
              >
                Start Draw
              </Button>
            </div>
          )}

          {/* Drawing State */}
          {drawState === 'drawing' && (
            <div className="text-center">
              {/* Countdown */}
              <div className="mb-8">
                <div className="flex items-center justify-center gap-2 text-sol-purple mb-2">
                  <Clock className="w-5 h-5" />
                  <span className="text-sm">Next number in</span>
                </div>
                <div className="font-mono text-5xl font-bold text-sol-green">
                  {formatTime(countdown)}
                </div>
              </div>

              {/* Progress */}
              <p className="text-white/60 mb-4">
                Number {revealedNumbers.length + 1} of 6
              </p>

              {/* Revealed Numbers */}
              <div className="flex flex-wrap gap-4 justify-center min-h-[100px]">
                {[0, 1, 2, 3, 4, 5].map((idx) => (
                  <div key={idx} className="relative">
                    <AnimatePresence mode="wait">
                      {revealedNumbers[idx] !== undefined ? (
                        <motion.div
                          key={`number-${revealedNumbers[idx]}`}
                          initial={{ scale: 0, rotate: -180, opacity: 0 }}
                          animate={{ scale: 1, rotate: 0, opacity: 1 }}
                          transition={{ type: 'spring', duration: 0.8 }}
                          className="w-16 h-16 rounded-full flex items-center justify-center font-mono text-2xl font-bold text-black"
                          style={{
                            background: 'linear-gradient(135deg, #14F195, #059669)',
                            boxShadow: '0 0 30px rgba(20, 241, 149, 0.6)',
                          }}
                        >
                          {revealedNumbers[idx]}
                        </motion.div>
                      ) : (
                        <motion.div
                          key={`placeholder-${idx}`}
                          animate={{ 
                            scale: idx === revealedNumbers.length ? [1, 1.1, 1] : 1,
                            borderColor: idx === revealedNumbers.length ? ['rgba(255,255,255,0.2)', 'rgba(20,241,149,0.5)', 'rgba(255,255,255,0.2)'] : 'rgba(255,255,255,0.2)'
                          }}
                          transition={{ repeat: Infinity, duration: 1.5 }}
                          className="w-16 h-16 rounded-full border-2 border-dashed flex items-center justify-center"
                        >
                          <span className="text-white/30 text-2xl">?</span>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Complete State */}
          {drawState === 'complete' && (
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6"
              >
                <Trophy className="w-16 h-16 text-sol-green mx-auto mb-4" />
                <h2 className="font-heading text-2xl font-bold mb-2">Draw Complete!</h2>
                <p className="text-white/60">The winning numbers are:</p>
              </motion.div>

              {/* Final Numbers */}
              <div className="flex flex-wrap gap-4 justify-center mb-8">
                {revealedNumbers.map((num, idx) => (
                  <motion.div
                    key={num}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: idx * 0.1, type: 'spring' }}
                    className="w-16 h-16 rounded-full flex items-center justify-center font-mono text-2xl font-bold text-black"
                    style={{
                      background: 'linear-gradient(135deg, #14F195, #059669)',
                      boxShadow: '0 0 30px rgba(20, 241, 149, 0.6)',
                    }}
                  >
                    {num}
                  </motion.div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-4 justify-center">
                <Button
                  onClick={() => navigate('/my-tickets')}
                  className="bg-sol-purple hover:bg-sol-purple/80"
                >
                  Check My Tickets
                </Button>
                <Button
                  onClick={startDraw}
                  variant="outline"
                  className="border-sol-green text-sol-green hover:bg-sol-green/10"
                >
                  New Draw
                </Button>
              </div>
            </div>
          )}
        </motion.div>

        {/* Info */}
        <p className="text-center text-white/40 text-sm">
          Each number is revealed with a {REVEAL_INTERVAL} second interval for maximum suspense!
        </p>
      </div>
    </div>
  );
};

export default LiveDraw;
